import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../App';
import PortalLayout from '../components/PortalLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { 
  ArrowRight, ArrowLeft, Check, Briefcase, 
  MapPin, DollarSign, FileText, Zap, Eye
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const JobWizard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    // Step 1: Job Details
    title: '',
    role_category: 'Technology',
    employment_type: 'full_time',
    location_suburb: '',
    location_state: 'NSW',
    location_postcode: '',
    work_arrangement: 'on_site',
    salary_min: '',
    salary_max: '',
    salary_unit: 'year',
    description: '',
    responsibilities: [],
    requirements: [],
    benefits: [],
    
    // Step 2: Branding (optional)
    logo_file: null,
    
    // Step 3: Plan & Add-ons
    plan_tier: 'free',
    boost_enabled: false,
    logo_enabled: false,
    
    // Calculated
    duration_days: 2,
    applicant_cap: 3,
    expires_at: null
  });

  const steps = [
    { number: 1, title: 'Job Details', icon: FileText },
    { number: 2, title: 'Branding', icon: Eye },
    { number: 3, title: 'Plan & Add-ons', icon: Zap },
    { number: 4, title: 'Review & Publish', icon: Check }
  ];

  const tiers = [
    { 
      id: 'free', 
      name: 'Free', 
      price: 0, 
      duration: 2, 
      cap: 3,
      abn_bonus: 5,  // +5 days if ABN verified
      features: ['Basic visibility', '2 days duration', 'Max 3 applications']
    },
    { 
      id: 'basic', 
      name: 'Basic', 
      price: 99, 
      duration: 15, 
      cap: 100,
      features: ['15 days duration', 'Up to 100 applications', 'Standard visibility']
    },
    { 
      id: 'standard', 
      name: 'Standard', 
      price: 199, 
      duration: 30, 
      cap: 200,
      features: ['30 days duration', 'Up to 200 applications', 'Priority visibility', 'Company logo']
    },
    { 
      id: 'premium', 
      name: 'Premium', 
      price: 399, 
      duration: 90, 
      cap: null,
      features: ['90 days duration', 'Unlimited applications', 'Featured placement', 'Company logo', 'Boost option']
    }
  ];

  const handleNext = () => {
    // Basic validation per step
    if (currentStep === 1) {
      if (!formData.title || !formData.location_suburb) {
        toast.error('Please fill in all required fields');
        return;
      }
    }
    
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handlePlanSelect = (tier) => {
    const selectedTier = tiers.find(t => t.id === tier.id);
    
    // Calculate expiry date
    const now = new Date();
    let duration = selectedTier.duration;
    
    // ABN bonus for free tier
    if (tier.id === 'free' && user?.employer_profile?.abn_verified) {
      duration += selectedTier.abn_bonus;
    }
    
    const expiresAt = new Date(now.getTime() + duration * 24 * 60 * 60 * 1000);
    
    setFormData({
      ...formData,
      plan_tier: tier.id,
      duration_days: duration,
      applicant_cap: selectedTier.cap,
      expires_at: expiresAt,
      logo_enabled: tier.id !== 'free'
    });
  };

  const handlePublish = async () => {
    setSaving(true);
    
    try {
      const jobData = {
        title: formData.title,
        role_category: formData.role_category,
        employment_type: formData.employment_type,
        location_suburb: formData.location_suburb,
        location_state: formData.location_state,
        location_postcode: formData.location_postcode,
        work_arrangement: formData.work_arrangement,
        salary_min: parseFloat(formData.salary_min) || null,
        salary_max: parseFloat(formData.salary_max) || null,
        salary_unit: formData.salary_unit,
        description: formData.description,
        plan_tier: formData.plan_tier,
        duration_days: formData.duration_days,
        applicant_cap: formData.applicant_cap,
        boost_enabled: formData.boost_enabled
      };

      const response = await axios.post(`${API}/portal/jobs/create`, jobData);
      
      toast.success('Job listing created successfully!');
      navigate(`/portal/gojobs/${response.data.job_id}`);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create job listing');
    } finally {
      setSaving(false);
    }
  };

  return (
    <PortalLayout>
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">Post a Job</h1>
          <p className="text-muted-foreground">Create and publish your job listing</p>
        </div>

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => (
              <React.Fragment key={step.number}>
                <div className="flex flex-col items-center">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center font-semibold ${
                    currentStep === step.number
                      ? 'bg-purple-600 text-white'
                      : currentStep > step.number
                      ? 'bg-green-600 text-white'
                      : 'bg-muted text-muted-foreground'
                  }`}>
                    {currentStep > step.number ? (
                      <Check className="w-6 h-6" />
                    ) : (
                      <step.icon className="w-6 h-6" />
                    )}
                  </div>
                  <span className={`text-sm mt-2 ${
                    currentStep === step.number ? 'font-semibold text-foreground' : 'text-muted-foreground'
                  }`}>
                    {step.title}
                  </span>
                </div>
                {index < steps.length - 1 && (
                  <div className={`flex-1 h-1 mx-4 ${
                    currentStep > step.number ? 'bg-green-600' : 'bg-muted'
                  }`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Step Content */}
        <Card>
          <CardContent className="p-8">
            {/* Step 1: Job Details */}
            {currentStep === 1 && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-foreground mb-6">Job Details</h2>
                
                <div>
                  <label className="text-sm font-medium text-foreground block mb-2">
                    Job Title <span className="text-red-500">*</span>
                  </label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="e.g., Senior Software Engineer"
                    required
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-foreground block mb-2">Category</label>
                    <select
                      value={formData.role_category}
                      onChange={(e) => setFormData({ ...formData, role_category: e.target.value })}
                      className="w-full px-3 py-2 border border-border rounded-lg bg-background"
                    >
                      <option value="Technology">Technology</option>
                      <option value="Healthcare">Healthcare</option>
                      <option value="Finance">Finance</option>
                      <option value="Retail">Retail</option>
                      <option value="Construction">Construction</option>
                      <option value="Education">Education</option>
                      <option value="Hospitality">Hospitality</option>
                      <option value="Manufacturing">Manufacturing</option>
                    </select>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-foreground block mb-2">Employment Type</label>
                    <select
                      value={formData.employment_type}
                      onChange={(e) => setFormData({ ...formData, employment_type: e.target.value })}
                      className="w-full px-3 py-2 border border-border rounded-lg bg-background"
                    >
                      <option value="full_time">Full-time</option>
                      <option value="part_time">Part-time</option>
                      <option value="contract">Contract</option>
                      <option value="casual">Casual</option>
                      <option value="temporary">Temporary</option>
                    </select>
                  </div>
                </div>

                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm font-medium text-foreground block mb-2">
                      City/Suburb <span className="text-red-500">*</span>
                    </label>
                    <Input
                      value={formData.location_suburb}
                      onChange={(e) => setFormData({ ...formData, location_suburb: e.target.value })}
                      placeholder="Sydney"
                      required
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-foreground block mb-2">State</label>
                    <select
                      value={formData.location_state}
                      onChange={(e) => setFormData({ ...formData, location_state: e.target.value })}
                      className="w-full px-3 py-2 border border-border rounded-lg bg-background"
                    >
                      <option value="NSW">NSW</option>
                      <option value="VIC">VIC</option>
                      <option value="QLD">QLD</option>
                      <option value="WA">WA</option>
                      <option value="SA">SA</option>
                      <option value="TAS">TAS</option>
                      <option value="ACT">ACT</option>
                      <option value="NT">NT</option>
                    </select>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-foreground block mb-2">Work Arrangement</label>
                    <select
                      value={formData.work_arrangement}
                      onChange={(e) => setFormData({ ...formData, work_arrangement: e.target.value })}
                      className="w-full px-3 py-2 border border-border rounded-lg bg-background"
                    >
                      <option value="on_site">On-site</option>
                      <option value="hybrid">Hybrid</option>
                      <option value="remote">Remote</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-foreground block mb-2">Salary Range (Optional)</label>
                  <div className="grid md:grid-cols-3 gap-4">
                    <Input
                      type="number"
                      value={formData.salary_min}
                      onChange={(e) => setFormData({ ...formData, salary_min: e.target.value })}
                      placeholder="Min"
                    />
                    <Input
                      type="number"
                      value={formData.salary_max}
                      onChange={(e) => setFormData({ ...formData, salary_max: e.target.value })}
                      placeholder="Max"
                    />
                    <select
                      value={formData.salary_unit}
                      onChange={(e) => setFormData({ ...formData, salary_unit: e.target.value })}
                      className="px-3 py-2 border border-border rounded-lg bg-background"
                    >
                      <option value="year">Per Year</option>
                      <option value="hour">Per Hour</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-foreground block mb-2">
                    Job Description <span className="text-red-500">*</span>
                  </label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Describe the role, responsibilities, and requirements..."
                    rows={8}
                    required
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Include: About the role, key responsibilities, requirements, and benefits
                  </p>
                </div>
              </div>
            )}

            {/* Step 2: Branding */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-foreground mb-6">Branding (Optional)</h2>
                
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
                  <p className="text-sm text-foreground">
                    Company logo helps your listing stand out. Available on paid plans.
                  </p>
                </div>

                <div>
                  <label className="text-sm font-medium text-foreground block mb-2">Company Logo</label>
                  <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
                    <div className="text-4xl mb-2">🏢</div>
                    <p className="text-sm text-muted-foreground mb-3">
                      Upload your company logo (PNG, JPG)
                    </p>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => setFormData({ ...formData, logo_file: e.target.files[0] })}
                      className="hidden"
                      id="logo-upload"
                    />
                    <label
                      htmlFor="logo-upload"
                      className="inline-block px-4 py-2 bg-muted hover:bg-muted/80 rounded-lg cursor-pointer text-sm font-medium"
                    >
                      Choose File
                    </label>
                    {formData.logo_file && (
                      <p className="text-sm text-foreground mt-2">{formData.logo_file.name}</p>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Logo will be displayed on your job listing (Standard plan and above)
                  </p>
                </div>
              </div>
            )}

            {/* Step 3: Plan Selection */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-foreground mb-6">Choose Your Plan</h2>

                {/* Free Tier ABN Notice */}
                {user?.employer_profile?.abn_verified && (
                  <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                    <p className="text-sm font-semibold text-green-800 dark:text-green-200">
                      ✓ ABN Verified - Your Free listings last 7 days (2 days + 5 day bonus)
                    </p>
                  </div>
                )}

                <div className="grid md:grid-cols-2 gap-4">
                  {tiers.map((tier) => (
                    <div
                      key={tier.id}
                      onClick={() => handlePlanSelect(tier)}
                      className={`cursor-pointer border-2 rounded-xl p-6 transition-all ${
                        formData.plan_tier === tier.id
                          ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                          : 'border-border hover:border-purple-300'
                      }`}
                    >
                      <div className="flex justify-between items-start mb-4">
                        <h3 className="text-xl font-bold text-foreground">{tier.name}</h3>
                        {tier.price === 0 ? (
                          <Badge className="bg-green-100 text-green-800">Free</Badge>
                        ) : (
                          <div className="text-right">
                            <div className="text-2xl font-bold text-foreground">${tier.price}</div>
                          </div>
                        )}
                      </div>

                      <ul className="space-y-2 mb-4">
                        {tier.features.map((feature, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm">
                            <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                            <span className="text-muted-foreground">{feature}</span>
                          </li>
                        ))}
                      </ul>

                      {tier.id === 'free' && user?.employer_profile?.abn_verified && (
                        <p className="text-xs text-green-600 dark:text-green-400 mt-2">
                          + 5 bonus days (ABN verified)
                        </p>
                      )}
                    </div>
                  ))}
                </div>

                {/* Add-ons */}
                {formData.plan_tier === 'premium' && (
                  <div className="mt-6">
                    <h3 className="font-semibold text-lg mb-4 text-foreground">Add-ons</h3>
                    <div className="space-y-3">
                      <label className="flex items-center gap-3 p-4 border border-border rounded-lg cursor-pointer hover:bg-muted">
                        <input
                          type="checkbox"
                          checked={formData.boost_enabled}
                          onChange={(e) => setFormData({ ...formData, boost_enabled: e.target.checked })}
                          className="w-5 h-5"
                        />
                        <div className="flex-1">
                          <div className="font-semibold text-foreground">Boost Listing (+$50)</div>
                          <p className="text-sm text-muted-foreground">Priority placement for 3 days</p>
                        </div>
                      </label>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Step 4: Review */}
            {currentStep === 4 && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-foreground mb-6">Review & Publish</h2>

                <div className="space-y-4">
                  <div>
                    <h3 className="font-semibold text-foreground mb-2">{formData.title}</h3>
                    <div className="flex flex-wrap gap-2">
                      <Badge>{formData.role_category}</Badge>
                      <Badge>{formData.employment_type.replace('_', '-')}</Badge>
                      <Badge>{formData.work_arrangement}</Badge>
                    </div>
                  </div>

                  <div>
                    <div className="text-sm text-muted-foreground">Location</div>
                    <div className="font-medium text-foreground">{formData.location_suburb}, {formData.location_state}</div>
                  </div>

                  {formData.salary_min && formData.salary_max && (
                    <div>
                      <div className="text-sm text-muted-foreground">Salary</div>
                      <div className="font-medium text-foreground">
                        ${formData.salary_min} - ${formData.salary_max} per {formData.salary_unit}
                      </div>
                    </div>
                  )}

                  <div>
                    <div className="text-sm text-muted-foreground">Plan</div>
                    <div className="font-medium text-foreground capitalize">{formData.plan_tier}</div>
                  </div>

                  <div>
                    <div className="text-sm text-muted-foreground">Duration</div>
                    <div className="font-medium text-foreground">{formData.duration_days} days</div>
                  </div>

                  <div>
                    <div className="text-sm text-muted-foreground">Applicant Limit</div>
                    <div className="font-medium text-foreground">
                      {formData.applicant_cap ? `${formData.applicant_cap} applications` : 'Unlimited'}
                    </div>
                  </div>

                  {formData.expires_at && (
                    <div>
                      <div className="text-sm text-muted-foreground">Expires</div>
                      <div className="font-medium text-foreground">
                        {new Date(formData.expires_at).toLocaleDateString()}
                      </div>
                    </div>
                  )}
                </div>

                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                  <p className="text-sm text-yellow-800 dark:text-yellow-200">
                    💡 Tip: Your job will go live immediately after publishing. Make sure all details are correct.
                  </p>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8 pt-6 border-t border-border">
              <button
                onClick={handleBack}
                disabled={currentStep === 1}
                className="flex items-center gap-2 px-6 py-3 border border-border rounded-lg hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ArrowLeft className="w-5 h-5" />
                Back
              </button>

              {currentStep < 4 ? (
                <button
                  onClick={handleNext}
                  className="flex items-center gap-2 px-6 py-3 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition-colors"
                >
                  Next
                  <ArrowRight className="w-5 h-5" />
                </button>
              ) : (
                <button
                  onClick={handlePublish}
                  disabled={saving}
                  className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                >
                  {saving ? 'Publishing...' : 'Publish Job'}
                  <Check className="w-5 h-5" />
                </button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </PortalLayout>
  );
};

export default JobWizard;
